<template>
    <div class="lx-online-booking-checkout-rental-agreement">
        <card
            v-if="website"
            :class="{ 'lx-online-booking-checkout-rental-agreement__card-danger': showRentalAgreementReminder }"
            :headerClass="['w-100']"
            :scrollable="website.is_rental_agreement_scrollable"
            :height="website.rental_agreement_content_height || 430"
        >
            <template #header-icon>
                <font-awesome-icon :icon="['fas', 'file-signature']" size="lg" />
            </template>
            <template #header-content> Rental Agreement </template>
            <template #body>
                <div v-if="!rentalAgreementBusy">
                    <b-row class="mb-4">
                        <b-col
                            class="lx-online-booking-checkout-rental-agreement__html"
                            v-html="rentalAgreementHTML"
                        ></b-col>
                    </b-row>
                    <card class="mb-3" nested>
                        <template #header-content> Sign and Accept </template>
                    </card>
                    <b-row>
                        <b-col cols="12">
                            <label><b>Guest IP:</b></label>
                        </b-col>
                        <b-col cols="12"> {{ client.guest_ip }} </b-col>
                    </b-row>
                    <b-row class="mt-3">
                        <b-col cols="12">
                            <label><b>Date and Time Stamp:</b></label>
                        </b-col>
                        <b-col cols="12">
                            {{ signedTimestamp }}
                        </b-col>
                    </b-row>
                    <b-row class="mt-3" no-gutters>
                        <b-col cols="12">
                            <label><b>Guest Signature:</b></label>
                        </b-col>
                        <b-col cols="12" v-if="!client.is_signed">
                            <div
                                class="lx-online-booking-checkout-rental-agreement__sign-here lx-online-booking-checkout-rental-agreement__sign-here-top"
                            >
                                Sign Here
                            </div>
                        </b-col>
                        <b-col cols="auto" v-if="!client.is_signed">
                            <validation-provider
                                name="signature"
                                rules="required"
                                v-slot="{ errors, handleChange }"
                            >
                                <form-input
                                    size="md"
                                    v-model="client.signature"
                                    floatingLabel="Enter your full name"
                                    invalidFeedback=""
                                    :error="errors[0]"
                                    @input="handleChange"
                                />
                            </validation-provider>
                        </b-col>
                        <b-col cols="auto" v-if="!client.is_signed">
                            <b-button
                                class="ml-2 lx-online-booking-checkout-rental-agreement__accept-button"
                                variant="primary"
                                size="md"
                                @click="sign"
                                :disabled="!client.signature"
                            >
                                I Accept
                            </b-button>
                        </b-col>
                        <b-col cols="12" v-if="!client.is_signed">
                            <div
                                class="lx-online-booking-checkout-rental-agreement__sign-here lx-online-booking-checkout-rental-agreement__sign-here-bottom"
                            >
                                Sign Here
                            </div>
                        </b-col>
                        <b-col v-if="client.is_signed">
                            <span class="lx-online-booking-checkout-rental-agreement__signature">
                                {{ client.signature }}
                            </span>
                        </b-col>
                    </b-row>
                    <b-row class="mt-3">
                        <b-col>
                            <b-button v-if="client.is_signed" class="mb-3" variant="light" @click="editSignature">
                                Edit signature
                            </b-button>
                            <p>
                                By clicking the "I Accept" button, you are agreeing to the terms and conditions
                                specified in the rental agreement shown above. All online bookings require a signed
                                rental agreement. Once signed and the booking completed, a signed copy of the rental
                                agreement will be emailed to you and a copy will be saved with your reservation.
                            </p>
                        </b-col>
                    </b-row>
                </div>
                <div v-else>
                    <div class="text-center">
                        <b-spinner class="lx-online-booking-checkout-rental-agreement__spinner" variant="primary" />
                    </div>
                </div>
            </template>
        </card>
        <div class="mt-2 lx-online-booking-checkout-rental-agreement__reminder" v-if="showRentalAgreementReminder">
            Please sign and accept the rental agreement.
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import Card from '../../../generic/card/card.vue';
import FormInput from '../../../generic/form-input/form-input.vue';

export default {
    name: 'OnlineBookingCheckoutRentalAgreement',
    components: { Card, FormInput },
    props: {
        errors: {
            type: Object,
            default: () => ({}),
        },
    },
    mounted() {
        (async () => {
            if (!this.settings) {
                this.$store.commit('onlineBooking/gotBusy', true);
                try {
                    await this.fetchSettings();
                } finally {
                    this.$store.commit('onlineBooking/gotBusy', false);
                }
            }
        })();
    },
    computed: {
        ...mapGetters('onlineBooking', [
            'client',
            'website',
            'settings',
            'reservation',
            'rentalAgreement',
            'rentalAgreementBusy',
            'countries',
            'states',
        ]),
        signedTimestamp() {
            if (!this.client.signed_timestamp) this.client.signed_timestamp = new Date();
            return format(this.client.signed_timestamp, 'EEEE, MMMM d, y h:mm a');
        },
        showRentalAgreementReminder() {
            const invalidFieldRefs = this.getInvalidFieldRefs();
            return !invalidFieldRefs.length && !this.client.is_signed;
        },
        rentalAgreementHTML() {
            if (this.rentalAgreement) {
                var ra = this.rentalAgreement;
                ra = ra.replace(/\[GUEST_NAMES\]/g, this.getGuestNames());
                ra = ra.replace(/\[GUESTCOMPANY\]/g, this.client.company);
                ra = ra.replace(/\[GUESTFIRSTNAME\]/g, this.client.first_name);
                ra = ra.replace(/\[GUESTLASTNAME\]/g, this.client.last_name);
                ra = ra.replace(
                    /\[GUEST_DATE_OF_BIRTH\]/g,
                    this.client.date_of_birth
                        ? format(parseISO(this.client.date_of_birth), this.settings.date_format)
                        : ''
                );
                ra = ra.replace(/\[GUEST_MOBILE_PHONE\]/g, this.client.mobile_phone);
                ra = ra.replace(/\[GUEST_PHONE\]/g, this.client.phone);
                ra = ra.replace(/\[GUEST_EMAIL\]/g, this.client.email);
                ra = ra.replace(/\[GUESTSTREETADDRESS\]/g, this.getAddress());
                ra = ra.replace(/\[GUESTCITY\]/g, this.client.city);
                ra = ra.replace(/\[GUESTCOUNTRY\]/g, this.getCountry());
                ra = ra.replace(/\[GUESTSTATEABBRV\]/g, this.getStateCode());
                ra = ra.replace(/\[GUESTPOSTALCODE\]/g, this.client.zip);
                ra = ra.replace(
                    /\[DS_GUEST_SIGNATURE\]/g,
                    this.client.is_signed
                        ? '<span class="lx-online-booking-checkout-rental-agreement__html__signature">' +
                              this.client.signature +
                              '</span>'
                        : ''
                );
                ra = ra.replace(
                    /\[DS_GUEST_INITIALS\]/g,
                    this.client.is_signed
                        ? '<span class="lx-online-booking-checkout-rental-agreement__html__initials">' +
                              this.getInitials(this.client.signature) +
                              '</span>'
                        : ''
                );

                var invoiceVars = this.client.invoice_variables;
                if (invoiceVars) {
                    for (var v, val, len = invoiceVars.length, i = 0; i < len; i++) {
                        v = invoiceVars[i];
                        if (v.input_type === 'date' && v.value) {
                            val = format(parseISO(v.value), this.settings.date_format);
                        } else {
                            val = v.value;
                        }
                        ra = ra.replace(new RegExp('\\[' + v.name + '\\]', 'g'), val);
                    }
                }
                return ra;
            }
            return '';
        },
    },
    methods: {
        async fetchSettings() {
            try {
                await this.$store.dispatch('onlineBooking/fetchSettings');
            } catch {
                this.errorToast({
                    text: 'Could not load settings.',
                });
            }
        },
        getInvalidFieldRefs() {
            const errors = Object.entries(this.errors)
                .map(([key, value]) => ({ key, value }))
                .filter(
                    (error) => (error['key'].includes('s1_') || error['key'].includes('s2_')) && error['value'].length
                )
                .map((error) => error['key']);
            return errors
        },
        sign() {
            const invalidFieldRefs = this.getInvalidFieldRefs();
            if (invalidFieldRefs.length) {
                document.querySelector(`*[name="${invalidFieldRefs[0]}"]`)?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
                this.warningToast({
                    text: 'Please fill in all required fields.',
                });
            } else {
                this.$store.commit('onlineBooking/gotClientIsSigned', true);
            }
        },
        editSignature() {
            this.client.is_signed = false;
        },
        getGuestNames() {
            if (!(this.client.tech_reservations instanceof Array)) {
                return '';
            }
            var nameList = [];
            var clientTechReservations = this.client.tech_reservations;
            var len = clientTechReservations.length;
            var i, j, techReservation, guestNames, gnlen, guestName;
            for (i = 0; i < len; i++) {
                techReservation = clientTechReservations[i];
                guestNames = techReservation.guest_names;
                if (guestNames instanceof Array) {
                    gnlen = guestNames.length;
                    for (j = 0; j < gnlen; j++) {
                        guestName = guestNames[j];
                        if (techReservation.is_require_guest_age) {
                            nameList.push(
                                ['<div>', guestName.name, ' &nbsp;&nbsp; ', guestName.age, '</div>'].join('')
                            );
                        } else {
                            nameList.push(['<div>', guestName.name, '</div>'].join(''));
                        }
                    }
                }
            }
            return nameList.join('');
        },
        getAddress() {
            var address = [];
            if (this.client.address1) {
                address.push(this.client.address1);
            }
            if (this.client.address2) {
                address.push(this.client.address2);
            }
            return address.join('<br>');
        },
        getCountry() {
            var countryId = this.client.country_id;
            if (countryId && countryId > 0) {
                var countries = this.countries;
                var len = countries.length;
                var country;
                for (var i = 0; i < len; i++) {
                    country = countries[i];
                    if (country.id == countryId) {
                        return country.name;
                    }
                }
            }
            return '';
        },
        getStateCode() {
            var stateId = this.client.state_id;
            if (stateId && stateId > 0) {
                var states = this.states;
                var len = states.length;
                var state;
                for (var i = 0; i < len; i++) {
                    state = states[i];
                    if (state.id == stateId) {
                        return state.code;
                    }
                }
            }
            return '';
        },
        getInitials(name) {
            var initials = name.match(/\b\w/g) || [];
            return ((initials.shift() || '') + (initials.pop() || '')).toUpperCase();
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

@font-face {
    font-family: 'Dancing Script';
    font-style: normal;
    font-weight: 400;
    src: local('Dancing Script'), local('DancingScript'), url(font-dancing-script.woff2) format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2212, U+2215,
        U+E0FF, U+EFFD, U+F000;
}

.lx-online-booking-checkout-rental-agreement {
    &__sign-here {
        display: inline-block;
        position: relative;
        border-radius: 6px;
        border: 4px solid #9fdff5;
        background: #39c0ed;
        line-height: 38px;
        vertical-align: middle;
        font-weight: bold;
        font-size: 17px;
        color: #fbfbfb;
        text-transform: uppercase;
    }

    &__sign-here:before,
    &__sign-here:after {
        border: solid transparent;
        border-radius: 2px;
        content: ' ';
        height: 0;
        width: 0;
        position: absolute;
        pointer-events: none;
    }

    &__sign-here-top {
        margin: 0 0 24px 0;
        padding: 0 8px;
    }

    &__sign-here-top:after,
    &__sign-here-top:before {
        top: 100%;
        left: 50%;
    }

    &__sign-here-top:after {
        border-color: rgba(221, 255, 51, 0);
        border-top-color: #39c0ed;
        border-width: 16px;
        margin-left: -16px;
    }
    &__sign-here-top:before {
        border-color: rgba(194, 225, 245, 0);
        border-top-color: #9fdff5;
        border-width: 22px;
        margin-left: -22px;
    }

    &__sign-here-bottom {
        margin: 5px 0 0 0;
        padding: 0 8px;
    }

    &__sign-here-bottom:after,
    &__sign-here-bottom:before {
        bottom: 100%;
        left: 50%;
    }

    &__sign-here-bottom:after {
        border-color: rgba(221, 255, 51, 0);
        border-bottom-color: #39c0ed;
        border-width: 16px;
        margin-left: -16px;
    }

    &__sign-here-bottom:before {
        border-color: rgba(194, 225, 245, 0);
        border-bottom-color: #9fdff5;
        border-width: 22px;
        margin-left: -22px;
    }

    &__signature {
        padding-left: 10px;
        font: 40px 'Dancing Script', cursive !important;
        white-space: nowrap !important;
    }

    &__accept-button {
        padding: 8px 12px 8px 12px !important;
        margin-bottom: 17px !important;
    }

    &__spinner {
        width: 3rem !important;
        height: 3rem !important;
    }

    &__reminder {
        color: #dc3545;
        font-size: 80%;
    }
}

:deep(*) .lx-online-booking-checkout-rental-agreement {
    &__card-danger.card {
        border: 1px solid #dc3545 !important;
        padding-bottom: 3px !important;
    }

    &__html {
        &__signature,
        &__initials {
            padding-left: 10px;
            font: 40px 'Dancing Script', cursive !important;
            white-space: nowrap !important;
        }

        th {
            padding: 5px !important;
        }
        td {
            padding: 5px !important;
        }

        table.brief_balance_table {
            border-collapse: collapse;
        }
        table.brief_balance_table * {
            width: 6cm;
        }
        table.brief_balance_table td {
            color: white;
            background-color: black;
            padding: 0.1cm 0.2cm 0 0.2cm;
            text-align: center;
            font-size: 12px;
            font-weight: bold;
        }
        table.brief_balance_table .brief_balance_container {
            background-color: white;
            padding: 10px;
            color: black;
        }
        table.brief_balance_table .brief_balance_last_row td {
            padding-bottom: 0.2cm;
        }
        table.brief_balance_table .small {
            font-size: 10px;
        }
        table.brief_balance_table .balance_span {
            font-size: 14px;
        }
    }
}
</style>
